import { Activity, ScrollText, Waypoints } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from '@/lib/scopedParams';
import { cn } from '@/lib/utils';
import { api } from '../../api';
import { Badge, type Tone } from '../../components/Badge';
import { Callout, Section } from '../../components/blocks';
import { DataError } from '../../components/DataError';
import { EntityRef } from '../../components/EntityRef';
import { KeyValue } from '../../components/KeyValue';
import { PageHeader, useDocumentTitle } from '../../components/PageHeader';
import { EmptyState, Loading } from '../../components/States';
import { formatCount } from '../../lib/format';
import { logsLink, traceSearchLink } from '../../lib/opsQuery';
import { useApi } from '../../lib/useApi';
import { LinkButton, RefreshButton, Since } from './shared';

export function eppLatencyQuery(registry?: string): string {
  const sel = registry ? `{registry="${registry}"}` : '';
  return `histogram_quantile(0.95, sum by (le, registry) (rate(registrar_epp_command_duration_seconds_bucket${sel}[5m])))`;
}

/** `/ops/registries`: registry connectivity and outcome backlog (spec §103). */
export function OpsRegistriesPage() {
  const { t } = useTranslation('ops');
  useDocumentTitle(t('registries.title'));
  const [params] = useSearchParams();
  const focus = params.get('registry');
  const result = useApi((signal) => api.listOpsRegistries({ signal }), []);

  return (
    <>
      <PageHeader title={t('registries.title')} subtitle={t('registries.subtitle')} actions={<RefreshButton onClick={result.reload} busy={result.status === 'loading'} />} />
      {result.status === 'loading' ? <Loading rows={3} /> : null}
      {result.status === 'error' ? <DataError error={result.error} onRetry={result.reload} title={t('registries.loadFailed')} /> : null}
      {result.status === 'success' && result.data.items.length === 0 ? <EmptyState title={t('registries.empty')} /> : null}
      {result.status === 'success' ? (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
          {result.data.items.map((r) => {
            const tone: Tone = r.unknown_transactions > 0 ? 'crit' : r.open_issues > 0 ? 'warn' : 'ok';
            const failedRecently = r.last_failure_at && (!r.last_success_at || r.last_failure_at > r.last_success_at);
            return (
              <Section
                key={r.code}
                className={cn('border-l-4', tone === 'crit' ? 'border-l-critical' : tone === 'warn' ? 'border-l-warning' : 'border-l-success', focus === r.code && 'ring-2 ring-ring')}
                title={<EntityRef label={r.code} sub={r.name} to={`/registry/registries/${encodeURIComponent(r.code)}`} />}
                actions={<Badge tone={tone}>{t(`registries.badge.${tone === 'crit' ? 'crit' : tone === 'warn' ? 'warn' : 'ok'}`)}</Badge>}
              >
                <div className="space-y-4">
                  <KeyValue
                    columns={2}
                    rows={[
                      [t('registries.field.open'), <span key="o" className="tabular text-lg font-semibold">{formatCount(r.open_transactions)}</span>],
                      [
                        t('registries.field.unknown'),
                        <span key="u" className={cn('tabular text-lg font-semibold', r.unknown_transactions > 0 && 'text-critical')}>
                          {formatCount(r.unknown_transactions)}
                        </span>,
                      ],
                      [t('registries.field.issues'), <span key="i" className={cn('tabular text-lg font-semibold', r.open_issues > 0 && 'text-warning')}>{formatCount(r.open_issues)}</span>],
                      [t('registries.field.lastSuccess'), <Since key="s" at={r.last_success_at} />],
                      [
                        t('registries.field.lastFailure'),
                        <span key="f" className="inline-flex flex-wrap items-center gap-2">
                          <Since at={r.last_failure_at} />
                          {failedRecently ? <Badge tone="warn">{t('registries.latest')}</Badge> : null}
                        </span>,
                      ],
                    ]}
                  />
                  <div className="flex flex-wrap gap-2">
                    <LinkButton to={`/ops/metrics?q=${encodeURIComponent(eppLatencyQuery(r.code))}&range=6h`}>
                      <Activity /> {t('registries.eppLatency')}
                    </LinkButton>
                    <LinkButton to={logsLink(`{stream="registry_exchange"} | json | registry="${r.code}"`, '1h')}>
                      <ScrollText /> {t('registries.logs')}
                    </LinkButton>
                    <LinkButton to={traceSearchLink({ registry: r.code }, '1h')}>
                      <Waypoints /> {t('registries.traces')}
                    </LinkButton>
                  </div>
                </div>
              </Section>
            );
          })}
        </div>
      ) : null}
      <Callout className="mt-6">{t('registries.note')}</Callout>
    </>
  );
}
